import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import * as Yup from 'yup';
import EditAtmAccessModal from './EditAtmAccessModal';
import EditCardholderName from './EditCardholderName';
import ManageAddressesModal from './ManageAddressesModal';
import AgreementsReviewForm from 'react/shared/components/AgreementsReviewForm';
import CardHeader from 'react/shared/components/true_link/lab/form/wizard/CardHeader';
import CardText from 'react/shared/components/true_link/lab/form/wizard/CardText';
import { oneLineAddress } from 'react/shared/utils/Address';
import { capitalize, possessiverize } from 'react/shared/utils/Strings';
import api from 'react/signups/card/api.js';
import ReviewRow from 'react/signups/card/common/ReviewRow';
import WizardCard from 'react/signups/card/common/WizardCard';
import userTypeShape from 'react/signups/card/shapes/UserType';
import { booleanToYesNo, yesNoToBoolean } from 'react/signups/card/utils';

const useStyles = makeStyles(() => ({
  paper: {
    background: '#58ACFD10',
    borderColor: '#58ACFD',
    padding: '10px',
  },
}));

export default function ReviewOrder({
  account,
  cardProgramQuestions,
  firstName,
  lastName,
  organization,
  reviewData,
  setAccount,
  setReviewData,
  setOrganization,
  submitting,
  user,
  userType,
}) {
  const classes = useStyles();
  const [showEditCardholderName, setShowEditCardholderName] = useState(false);
  const [showEditShipping, setShowEditShipping] = useState(false);
  const [showEditAtmAccess, setShowEditAtmAccess] = useState(false);

  const validationSchema = Yup.object().shape({
    agreements: Yup.array().test(
      'signed-all',
      'Please confirm all agreements',
      (value) =>
        value.includes('esignAgreement') &&
        value.includes('msaAgreement') &&
        (!account.requiresCardholderAgreement || value.includes('cardholderAuthorized')),
    ),
  });

  const initialValues = {
    agreements: reviewData.agreements || [],
  };

  const handleContinue = (values) => {
    setReviewData({
      agreements: values.agreements,
    });
  };

  const handleEditCardholderName = () => {
    setShowEditCardholderName(true);
  };

  const handleEditShipping = () => {
    setShowEditShipping(true);
  };

  const handleEditAtmAccess = () => {
    setShowEditAtmAccess(true);
  };

  const setAccountWithResponse = (response) => {
    const accountData = response.data.data;
    const { id, relationships, attributes } = accountData;
    const { cardholder, shipping_address } = relationships;
    setAccount({
      id,
      ...attributes,
      cardholder: {
        id: cardholder.data.id,
        ...cardholder.data.attributes,
        address: {
          id: cardholder.data.relationships.address.data,
          ...cardholder.data.relationships.address.data.attributes,
        },
      },
      shippingAddress: { id: shipping_address.data.id, ...shipping_address.data.attributes },
    });
  };

  const updateOrganizationAddress = (values) => {
    const addressDetails = values.address;
    api
      .updateUser(user.id, {
        organization: {
          addressesAttributes: [
            {
              city: addressDetails.city,
              state: addressDetails.state,
              street1: addressDetails.street1,
              street2: addressDetails.street2,
              zip: addressDetails.zip,
            },
          ],
        },
      })
      .then((response) => {
        const organization = response.data.organization.data;

        setOrganization({
          id: organization.id,
          ...organization.attributes,
          address: {
            id: organization.relationships.address.data.id,
            ...organization.relationships.address.data.attributes,
          },
        });
      });
  };

  const handleCardholderNameChange = (name) => {
    api
      .updateAccount(account.id, { cardOrder: { cardholderAttributes: { cardName: name } } })
      .then((response) => {
        setAccountWithResponse(response);

        setShowEditCardholderName(false);
      });
  };

  const handleAddressChange = (values, shippingDestination) => {
    const addressDetails = values.address;
    api
      .updateAccount(account.id, {
        cardOrder: {
          addressAttributes: {
            city: addressDetails.city,
            state: addressDetails.state,
            street1: addressDetails.street1,
            street2: addressDetails.street2,
            zip: addressDetails.zip,
          },
          shippingDestination,
        },
      })
      .then((response) => {
        setAccountWithResponse(response);
      });
    if (shippingDestination === 'office') {
      updateOrganizationAddress(values);
    }
  };

  const handleAtmAccessChange = (access) => {
    api
      .updateAccount(account.id, {
        cardOrder: {
          atmAccess: yesNoToBoolean(access),
        },
      })
      .then((response) => {
        setAccountWithResponse(response);

        setShowEditAtmAccess(false);
      });
  };

  const fullName = `${firstName} ${lastName}`;

  return (
    <WizardCard>
      <CardHeader>Review your order</CardHeader>
      <CardText>Please make sure everything looks correct</CardText>
      <hr />
      {!showEditCardholderName && (
        <ReviewRow
          className="nameOnCard"
          label="Name on card"
          onEdit={account.cardholder.cardName ? handleEditCardholderName : undefined}
          value={<CardText>{account.cardholder.cardName || fullName}</CardText>}
        />
      )}
      {showEditCardholderName && (
        <EditCardholderName
          cardholderName={account.cardholder.cardName}
          onSubmit={handleCardholderNameChange}
        />
      )}
      {account.cardholder.cardName && (
        <Paper className={classes.paper} variant="outlined">
          <div>We’ve abbreviated {possessiverize(firstName)} name so it fits on the card.</div>
          <div>Feel free to edit!</div>
        </Paper>
      )}
      <hr />
      <ReviewRow
        className="shippingAddress"
        label="Ship to"
        onEdit={handleEditShipping}
        value={<CardText>{oneLineAddress(account.shippingAddress)}</CardText>}
      />
      {showEditShipping && (
        <ManageAddressesModal
          account={account}
          cardholderFirstName={firstName}
          onChangeAddress={handleAddressChange}
          onClose={() => setShowEditShipping(false)}
          organization={organization}
          userType={userType}
        />
      )}
      <hr />
      {!account.fundedByTrust && cardProgramQuestions.atm_access && (
        <>
          <ReviewRow
            className="atmAccess"
            label="ATM access"
            onEdit={handleEditAtmAccess}
            value={<CardText>{capitalize(booleanToYesNo(account.atmAccess))}</CardText>}
          />
          <hr />
        </>
      )}
      {showEditAtmAccess && (
        <>
          <EditAtmAccessModal
            atmAccess={booleanToYesNo(account.atmAccess)}
            firstName={firstName}
            onChangeAtmAccess={handleAtmAccessChange}
            onClose={() => setShowEditAtmAccess(false)}
          />
          <hr />
        </>
      )}
      <Formik
        initialValues={initialValues}
        onSubmit={handleContinue}
        validationSchema={validationSchema}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <AgreementsReviewForm
            msaLink={account.msaLink}
            onContinue={handleSubmit}
            requiresCardholderAgreement={account.requiresCardholderAgreement}
            setFieldValue={setFieldValue}
            submitting={submitting}
            values={values}
          />
        )}
      </Formik>
    </WizardCard>
  );
}

ReviewOrder.propTypes = {
  account: PropTypes.object.isRequired,
  cardProgramQuestions: PropTypes.object.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  organization: PropTypes.object.isRequired,
  reviewData: PropTypes.object.isRequired,
  setAccount: PropTypes.func.isRequired,
  setReviewData: PropTypes.func.isRequired,
  setOrganization: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  userType: userTypeShape.isRequired,
};
